import React, { useState, useRef, useEffect } from 'react';
import type { UserField } from "types/UserZendesk";
import { FaRegArrowAltCircleDown } from 'react-icons/fa';

interface MultiSelectFieldProps {
  field: UserField;
  value: string[];
  onChange: (value: string[]) => void;
}

export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  field,
  value = [],
  onChange,
}) => {
  const safeValue = value ?? [];
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const toggleOption = (opt: string) => {
    const next = safeValue.includes(opt)
      ? safeValue.filter(v => v !== opt)
      : [...safeValue, opt];
    onChange(next);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative mb-4" ref={containerRef}>
      <label className="block text-gray-700 mb-1">{field.title}</label>

      <button
        type="button"
        className={`w-full flex flex-wrap items-center min-h-[2.5rem] px-3 py-1 border rounded
          ${open ? 'border-blue-500' : 'border-gray-300'} focus:outline-none`}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => e.key === 'Enter' && setOpen(o => !o)}
      >
        {safeValue.length === 0 && (
          <span className="text-gray-400">Selecione...</span>
        )}

        {safeValue.map(val => {
          const opt = field.custom_field_options?.find(o => o.value === val);
          return (
            <span
              key={val}
              className="flex items-center bg-gray-200 text-gray-800 text-sm rounded-full px-2 py-0.5 mr-1 mb-1"
            >
              {opt?.name || val}
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  toggleOption(val);
                }}
                className="ml-1 text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </span>
          );
        })}

        <span className="ml-auto transform transition-transform"
              style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', cursor: 'pointer' }}>
          <FaRegArrowAltCircleDown />
        </span>
      </button>

      {open && (
        <ul className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded shadow-lg">
          {field.custom_field_options?.map(option => (
            <li
              key={option.id}
              className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
              onClick={() => toggleOption(option.value)}
            >
              <input
                type="checkbox"
                checked={safeValue.includes(option.value)}
                readOnly
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="ml-2 text-gray-700">{option.name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
